import Dexie, { type Table } from 'dexie'
import type { ImportedCity, Plan, Snapshot, Settings } from '$lib/types'

const VALID_V3_THEMES = new Set<string>(['dark', 'light', 'dark-blue', 'light-blue'])

export class NexusForgeDB extends Dexie {
  imports!: Table<ImportedCity, number>
  plans!: Table<Plan, number>
  snapshots!: Table<Snapshot, number>
  settings!: Table<Settings, string>

  constructor() {
    super('nexus-forge')

    // Version 1 — initial schema.
    // Add new versions BELOW this one. Do not modify a published version.
    this.version(1).stores({
      imports: '++id, name, importedAt',
      plans: '++id, importedCityId, name, updatedAt',
      snapshots: '++id, planId, takenAt',
      settings: 'key',
    })

    // Version 2 — backfill `Plan.nextEntityId` for the editor's id allocator.
    // Schema is unchanged; this is a data-only upgrade. The counter starts
    // one above the largest id seen across `buildings` and `inventory`, so
    // future vended ids cannot collide with anything already in the plan.
    this.version(2)
      .stores({
        imports: '++id, name, importedAt',
        plans: '++id, importedCityId, name, updatedAt',
        snapshots: '++id, planId, takenAt',
        settings: 'key',
      })
      .upgrade(async (tx) => {
        await tx
          .table('plans')
          .toCollection()
          .modify((plan: Plan) => {
            if (typeof plan.nextEntityId === 'number') return
            plan.nextEntityId = nextIdFromExisting(plan)
          })
      })

    // Version 3 — Phase 2.5 theme widening. The old enum `'light' | 'dark' |
    // 'system'` is replaced by `'dark' | 'light' | 'dark-blue' | 'light-blue'`.
    // The v2 'light' was a different palette from the v3 'light' (different
    // chrome, different unlocked-area fill), so we migrate it to 'dark' rather
    // than silently re-styling the user's saved choice. 'system' goes to 'dark'
    // for the same reason — none of the four new themes follows OS preference,
    // so there's no faithful translation.
    this.version(3)
      .stores({
        imports: '++id, name, importedAt',
        plans: '++id, importedCityId, name, updatedAt',
        snapshots: '++id, planId, takenAt',
        settings: 'key',
      })
      .upgrade(async (tx) => {
        await tx
          .table('settings')
          .toCollection()
          .modify((s: { theme?: string }) => {
            // Legacy values get remapped to dark even when the string
            // overlaps a new key. The v2 'light' was a different palette
            // (no sage unlocked area, no bronze chrome); silently re-styling
            // the user's saved choice would surprise them. The v3 set has
            // no OS-following theme, so 'system' has no faithful translation
            // either. Both go to dark — the safety-net default.
            if (s.theme === 'system' || s.theme === 'light') {
              s.theme = 'dark'
              return
            }
            if (typeof s.theme !== 'string' || !VALID_V3_THEMES.has(s.theme)) {
              s.theme = 'dark'
            }
          })
      })
  }
}

function nextIdFromExisting(plan: Plan): number {
  let max = 0
  for (const b of plan.buildings ?? []) {
    if (typeof b.id === 'number' && b.id > max) max = b.id
  }
  for (const i of plan.inventory ?? []) {
    if (typeof i.id === 'number' && i.id > max) max = i.id
  }
  return max + 1
}

export const db = new NexusForgeDB()
